import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import {
  ArrowRight,
  Globe,
  Layers,
  MousePointerClick,
  Package,
  Presentation,
  Sparkles,
  Terminal
} from 'lucide-react';
import React from 'react';
import { DemoButton } from '../components/DemoButton';
import { Faq } from '../components/Faq';
import { Headline } from '../components/Headline';
import { mobileHeroLeadText } from '../components/MarketingTypography';
import { GradientBackground } from '../components/HeroSection/GradientBackground';
import { LogosSection } from '../components/LogosSection';
import { Reveal } from '../components/motion';
import { EarlyAccessBadge } from '../components/NewBadge';
import { PricingTable } from '../components/PricingTable';
import { ProductIcon } from '../components/ProductIcon';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const HeroSection = styled('section')`
  position: relative;
  overflow: hidden;
  padding: 120px 0 88px;
  text-align: center;

  @media (max-width: 800px) {
    padding: 88px 0 64px;
  }
`;

const HeroCopy = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto 56px;
  padding: 0 24px;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 620px;
  margin: 24px auto 0;
  ${mobileHeroLeadText}
`;

const QuoteSection = styled('section')`
  max-width: 720px;
  margin: 96px auto 0;
  padding: 0 24px;
`;

const QuoteCard = styled('article')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
`;

const QuoteLogo = styled('img')`
  height: 28px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  object-position: left center;
`;

const Quote = styled('blockquote')`
  margin: 0;
  font-family: 'Merriweather', serif;
  font-size: 17px;
  line-height: 1.65;
  color: #0e1c2b;
  font-style: italic;
`;

const QuoteAuthorBlock = styled('div')`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
`;

const QuoteAuthorName = styled('p')`
  font-size: 14px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const QuoteAuthorTitle = styled('p')`
  font-size: 13px;
  color: #64748b;
  margin: 3px 0 0;
`;

const CapabilitySection = styled('section')`
  max-width: 1100px;
  margin: 96px auto;
  padding: 0 24px;
`;

const CapabilityGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 48px;
  align-items: stretch;

  > * {
    height: 100%;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const capabilityCardStyles = `
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
`;

const CapabilityCard = styled('div')`
  ${capabilityCardStyles}
`;

const CapabilityCardLink = styled(Link)`
  ${capabilityCardStyles}
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const CapabilityTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const CapabilityBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
  flex: 1;
`;

const CapabilityLinkRow = styled('div')`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CapabilityAiCardLink = styled(Link)`
  ${capabilityCardStyles}
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  margin-top: 20px;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const CapabilityAiContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const CapabilityAiTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FaqSection = styled('section')`
  max-width: 1100px;
  margin: 96px auto;
  padding: 0 24px;
`;

const FinalCta = styled('section')`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const CtaDescription = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  max-width: 560px;
  margin: 16px auto 28px;
`;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          title="Affilimate Pricing"
          description="Affilimate plans for premium publishers, creators, and merchants and agencies. Commerce analytics, sponsorship campaign infrastructure, and early-access AI tools built on your commerce data."
          pathname="/pricing/"
        />

        <HeroSection>
          <GradientBackground />
          <HeroCopy>
            <Reveal>
              <Headline
                variant="h1"
                component="h1"
                style={{
                  maxWidth: '700px',
                  margin: '0 auto',
                  fontWeight: 900
                }}
              >
                Plans tailored to your business
              </Headline>
              <HeroDescription>
                What category does your business fall into? Pick the plan that
                matches how you work, and we&apos;ll tailor the walkthrough to
                your setup.
              </HeroDescription>
            </Reveal>
          </HeroCopy>
          <PricingTable />
        </HeroSection>

        <LogosSection />

        <QuoteSection>
          <Reveal>
            <QuoteCard>
              <QuoteLogo src="/images/logos/usa-today.svg" alt="USA Today" />
              <Quote>
                The level of detail we can see now is a total game-changer.
                We&apos;re able to make smarter, faster decisions, and it&apos;s
                showing up in the revenue.
              </Quote>
              <QuoteAuthorBlock>
                <QuoteAuthorName>Jim Ryan</QuoteAuthorName>
                <QuoteAuthorTitle>
                  Sr Director of Affiliate Strategy, Commerce Content
                </QuoteAuthorTitle>
              </QuoteAuthorBlock>
            </QuoteCard>
          </Reveal>
        </QuoteSection>

        <CapabilitySection>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Headline component="h2">What&apos;s included</Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '600px', margin: '16px auto 0' }}
              >
                Everything you need for a modern commerce program
              </Typography>
            </div>
          </Reveal>

          <CapabilityGrid>
            <Reveal delay={0} style={{ height: '100%' }}>
              <CapabilityCardLink to="/product/unify/">
                <ProductIcon $size="lg">
                  <Layers size={20} />
                </ProductIcon>
                <CapabilityTitle>Unify</CapabilityTitle>
                <CapabilityBody>
                  All your transactions, rates, and payouts from 100+ networks
                  in one place. Use it through the dashboard, API, or MCP.
                </CapabilityBody>
                <CapabilityLinkRow>
                  Explore Unify
                  <ArrowRight size={14} />
                </CapabilityLinkRow>
              </CapabilityCardLink>
            </Reveal>

            <Reveal delay={60} style={{ height: '100%' }}>
              <CapabilityCardLink to="/product/optimize/">
                <ProductIcon $size="lg">
                  <MousePointerClick size={20} />
                </ProductIcon>
                <CapabilityTitle>Optimize</CapabilityTitle>
                <CapabilityBody>
                  Article and link-level revenue, conversion heatmaps, content
                  segmentation, traffic source and UTM reporting, and 25+
                  customizable attributes.
                </CapabilityBody>
                <CapabilityLinkRow>
                  Explore Optimize
                  <ArrowRight size={14} />
                </CapabilityLinkRow>
              </CapabilityCardLink>
            </Reveal>

            <Reveal delay={120} style={{ height: '100%' }}>
              <CapabilityCardLink to="/product/amplify/">
                <ProductIcon $size="lg">
                  <Presentation size={20} />
                </ProductIcon>
                <CapabilityTitle>Amplify</CapabilityTitle>
                <CapabilityBody>
                  Campaign infrastructure for sponsorship and link swap
                  campaigns, from pitch to renewal.
                </CapabilityBody>
                <CapabilityLinkRow>
                  Explore Amplify
                  <ArrowRight size={14} />
                </CapabilityLinkRow>
              </CapabilityCardLink>
            </Reveal>

            <Reveal delay={180} style={{ height: '100%' }}>
              <CapabilityCard>
                <ProductIcon $size="lg">
                  <Package size={20} />
                </ProductIcon>
                <CapabilityTitle>Product data</CapabilityTitle>
                <CapabilityBody>
                  Enrich your reporting with prices, categories, and
                  availability from 70M+ tracked products matched to your
                  links.
                </CapabilityBody>
              </CapabilityCard>
            </Reveal>

            <Reveal delay={240} style={{ height: '100%' }}>
              <CapabilityCard>
                <ProductIcon $size="lg">
                  <Globe size={20} />
                </ProductIcon>
                <CapabilityTitle>Offsite tracking</CapabilityTitle>
                <CapabilityBody>
                  Follow revenue beyond your site: mobile apps, syndication
                  partners like Apple News and MSN, and social channels like
                  Instagram and YouTube.
                </CapabilityBody>
              </CapabilityCard>
            </Reveal>

            <Reveal delay={300} style={{ height: '100%' }}>
              <CapabilityCard>
                <ProductIcon $size="lg">
                  <Terminal size={20} />
                </ProductIcon>
                <CapabilityTitle>API, MCP, Data warehouse</CapabilityTitle>
                <CapabilityBody>
                  Programmatic access to your commerce data everywhere you need
                  it. Plus, automations you can integrate into your CMS and
                  custom tracking solutions.
                </CapabilityBody>
              </CapabilityCard>
            </Reveal>
          </CapabilityGrid>

          <Reveal delay={120}>
            <CapabilityAiCardLink to="/ai-platform/">
              <ProductIcon $size="lg">
                <Sparkles size={20} />
              </ProductIcon>
              <CapabilityAiContent>
                <CapabilityAiTitleRow>
                  <CapabilityTitle>AI platform</CapabilityTitle>
                  <EarlyAccessBadge />
                </CapabilityAiTitleRow>
                <CapabilityBody>
                  Evolve your commerce workflows with AI: agentic campaign
                  management, MCP-enabled integrations, and yield optimization,
                  grounded in your commerce data. A single platform building for
                  the next era of commerce for publishers.
                </CapabilityBody>
                <CapabilityLinkRow>
                  Explore the AI platform
                  <ArrowRight size={14} />
                </CapabilityLinkRow>
              </CapabilityAiContent>
            </CapabilityAiCardLink>
          </Reveal>
        </CapabilitySection>

        <FaqSection>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Headline component="h2">Frequently asked questions</Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '560px', margin: '16px auto 0' }}
              >
                Quick answers about setup, integrations, and how Affilimate fits
                into your stack.
              </Typography>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <Faq />
          </Reveal>
        </FaqSection>

        <FinalCta>
          <Reveal>
            <Headline component="h2">Not sure where you fit?</Headline>
            <CtaDescription>
              Get a demo and bring your questions and goals. We'll share how
              publishers are using Affilimate to grow their commerce programs.
            </CtaDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
